import React, { useEffect, useState, useCallback } from "react";
import { FiMonitor, FiSmartphone, FiTablet, FiX, FiLogOut } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { apiRequest } from "../utils/apiRequest";
import { useToast } from "./Toast";

const deviceIcon = (deviceType) => {
  if (deviceType === "mobile") return <FiSmartphone size={20} />;
  if (deviceType === "tablet") return <FiTablet size={20} />;
  return <FiMonitor size={20} />;
};

const timeAgo = (iso) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

/**
 * Renders one user's active device sessions and lets the caller revoke them.
 *
 * - Self-service: listUrl="{SESSIONS}", revokeUrl=(id)=>`${SESSIONS}/${id}` — the
 *   caller identifies "this device" via the x-refresh-token header (see apiRequest).
 * - Admin (viewing another user): listUrl=`${USERS}/:id/sessions`,
 *   revokeUrl=(id)=>`${USERS}/:id/sessions/${id}`, no "this device" badge,
 *   optional bulkRevokeUrl for a "Log out everywhere" button.
 */
const SessionList = ({ listUrl, revokeUrl, bulkRevokeUrl, emptyLabel = "No active devices." }) => {
  const { token } = useAuth();
  const toast = useToast();
  const [sessions, setSessions] = useState(null); // null = loading
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchSessions = useCallback(async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    const res = await apiRequest(
      listUrl,
      { headers: refreshToken ? { "x-refresh-token": refreshToken } : {} },
      token
    );
    if (res?.ok) setSessions(res.data);
    else { setSessions([]); toast.error(res?.data?.message || "Could not load devices."); }
  }, [listUrl, token, toast]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleRevoke = (session) => {
    toast.deleteConfirm(
      `Log out "${session.device?.browser || "this device"}" (${session.device?.os || "Unknown"})?`,
      async () => {
        setBusyId(session.id);
        const res = await apiRequest(revokeUrl(session.id), { method: "DELETE" }, token);
        setBusyId(null);
        if (res?.ok) { toast.success("Device logged out."); fetchSessions(); }
        else toast.error(res?.data?.message || "Could not revoke device.");
      },
      "Log Out Device"
    );
  };

  const handleRevokeAll = () => {
    toast.deleteConfirm(
      "Log out this user from every device? They will need to sign in again.",
      async () => {
        setBulkBusy(true);
        const res = await apiRequest(bulkRevokeUrl, { method: "DELETE" }, token);
        setBulkBusy(false);
        if (res?.ok) { toast.success("Logged out everywhere."); fetchSessions(); }
        else toast.error(res?.data?.message || "Could not revoke devices.");
      },
      "Log Out Everywhere"
    );
  };

  if (sessions === null) {
    return <div style={{ padding: "1rem", color: "#888" }}>Loading devices…</div>;
  }

  return (
    <div>
      {bulkRevokeUrl && sessions.length > 0 && (
        <button
          type="button"
          className="Login-btn"
          onClick={handleRevokeAll}
          disabled={bulkBusy}
          style={{
            display: "flex", alignItems: "center", gap: 6, marginBottom: "0.75rem",
            background: "#fee2e2", color: "#991b1b", border: "none",
            borderRadius: 6, padding: "6px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >
          <FiLogOut size={14} /> {bulkBusy ? "Logging out…" : "Log out everywhere"}
        </button>
      )}

      {sessions.length === 0 ? (
        <div style={{ padding: "1rem", color: "#888" }}>{emptyLabel}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {sessions.map((s) => (
            <div
              key={s.id}
              style={{
                display: "flex", alignItems: "center", gap: "0.75rem",
                border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px",
              }}
            >
              <div style={{ color: "#1a2a4d" }}>{deviceIcon(s.device?.deviceType)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  {s.device?.browser || "Unknown browser"} · {s.device?.os || "Unknown OS"}
                  {s.isCurrent && (
                    <span style={{
                      fontSize: 11, fontWeight: 600, color: "#166534",
                      background: "#dcfce7", borderRadius: 999, padding: "1px 8px",
                    }}>This device</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {s.ip || "Unknown IP"} · last active {timeAgo(s.lastActiveAt)}
                </div>
              </div>
              {!s.isCurrent && (
                <button
                  type="button"
                  onClick={() => handleRevoke(s)}
                  disabled={busyId === s.id}
                  title="Log out this device"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#991b1b", padding: 6, borderRadius: 6,
                  }}
                >
                  <FiX size={18} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionList;
